import React, { useState, useEffect, useRef, useCallback } from "react";
import { Col, Form, Image, Row, Spinner } from "react-bootstrap";
import {
  Cart,
  ArrowUpShort,
  ArrowDownShort,
  CheckCircle,
  Building,
  House,
  Buildings,
  GeoAlt,
  ExclamationTriangle,
  ChevronDown,
  Clock,
} from "react-bootstrap-icons";
import { useShoppingCart } from "../context/ShoppingCartContext";
import { useServiceAvailability } from "../context/ServiceAvailabilityContext";
import { formatCurrency } from "../utilities/formatCurrency";
import { calculateSubtotal, fetchStoreLocation } from "../lib/calculator.jsx";
import { applyFreeDeliveryDiscount } from "../lib/discounts/freeDeliveryThreshold.jsx";
import { applyFirstOrderFreeDelivery } from "../lib/discounts/firstOrderFreeDelivery.jsx";
import { applyFirstOrderDiscount, isFirstTimeCustomer } from "../lib/discounts/firstOrderDiscount.jsx";
import { applyWalletCredit, fetchWalletBalance } from "../lib/discounts/referralCredits.jsx";
import { calculateOrderTotal, calculateServiceFee } from "../lib/orderCost";
import { fetchProduct, fetchProductOptionItem } from "../lib/webService/webService";
import MapInterface from "../components/MapInterface";
import CheckoutSubmitButton from "../components/checkout/CheckoutSubmitButton";
import { RANGE_STATUS } from "../components/checkout/checkoutSubmitButtonState";
import toast from "../utilities/toast";
import { auth } from "../lib/config/firebaseInit";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate, useLocation } from "react-router-dom";
import { goToLogin } from "../utilities/authRedirect";
import { useFormDraft } from "../hooks/useFormDraft";
import mpesaLogo from "/imgs/M-PESA-logo-2.png";
import { isPhone } from "../utilities/format";
import { PullToRefreshWrapper } from "../components/pullRefresh";
import { Seo } from "../components/Seo";
import { generateNoIndexSEO } from "../lib/seo/metaGenerators";
import { useCachedImageSrc } from "../hooks/useCachedImageSrc";

// ─── Constants ────────────────────────────────────────────────────────────────

const FONT =
  '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", system-ui, sans-serif';

const FORM_FIELDS = {
  firstName:    { required: true,  minLength: 2, maxLength: 20 },
  lastName:     { required: true,  minLength: 2, maxLength: 20 },
  phone:        { required: true,  pattern: /^[0-9]{10}$/ },
  buildingType: { required: true  },
  unitFloor:    { required: false },
  buildingName: { required: false },
};

const FIELD_LABELS = {
  firstName: "First name",
  lastName: "Last name",
  phone: "Phone number",
  buildingType: "Building type",
};

// Single source of truth for FORM_FIELDS rule violations — used both for the
// submit-time toast and for inline field feedback (see getInlineFieldError).
function getFieldError(field, value) {
  const rules = FORM_FIELDS[field];
  const label = FIELD_LABELS[field] || field;

  if (!value) return rules.required ? `${label} is required` : null;
  if (rules.minLength && value.length < rules.minLength)
    return `${label} must be at least ${rules.minLength} characters`;
  if (rules.maxLength && value.length > rules.maxLength)
    return `${label} must not exceed ${rules.maxLength} characters`;
  if (rules.pattern && !rules.pattern.test(value))
    return field === "phone" ? "Enter a valid 10-digit phone number" : `${label} is invalid`;
  return null;
}

// Inline, per-field feedback must only reflect data the user actually typed —
// an empty/untouched field is never "wrong", it's just not filled in yet, so
// this suppresses the "required" case that getFieldError otherwise reports.
function getInlineFieldError(field, value) {
  return value ? getFieldError(field, value) : null;
}

const DELIVERY_PRICING = {
  BASE_FEE: 80,
  INCLUDED_DISTANCE_KM: 1.5,
  PRICE_PER_KM: 18,
  PRICE_PER_MINUTE: 0.8,
  MIN_DELIVERY_FEE: 80,
  MAX_DELIVERY_FEE: 350,
  MAX_DELIVERY_DISTANCE_KM: 20,
};

const METERS_PER_KILOMETER = 1000;
const SECONDS_PER_MINUTE = 60;

const metersToKilometers = (meters) =>
  typeof meters === "number" ? meters / METERS_PER_KILOMETER : 0;

const secondsToMinutes = (seconds) =>
  typeof seconds === "number" ? seconds / SECONDS_PER_MINUTE : 0;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const calculateDistanceCharge = (distanceKm) => {
  const billableDistance = Math.max(distanceKm - DELIVERY_PRICING.INCLUDED_DISTANCE_KM, 0);
  return billableDistance * DELIVERY_PRICING.PRICE_PER_KM;
};

const calculateDurationCharge = (durationMinutes) =>
  durationMinutes * DELIVERY_PRICING.PRICE_PER_MINUTE;

// Re-derives serviceFee, walletCredit and total from the order's components,
// so no single update can leave the total out of step with its lines.
const priceOrder = (state, walletBalance) => {
  const serviceFee = calculateServiceFee(state.subSum);
  const beforeWallet = calculateOrderTotal({
    subTotal:    state.subSum,
    serviceFee,
    deliveryFee: state.deliveryAmount,
    tax:         state.taxAmount,
    discount:    state.firstOrderDiscount,
  });
  const { credit: walletCredit } = applyWalletCredit(beforeWallet, walletBalance);
  return { ...state, serviceFee, walletCredit, total: beforeWallet - walletCredit };
};

const EMPTY_ROUTE_DETAILS = {
  start: "",
  end: "",
  duration: { text: "", value: 0 },
  distance: { text: "", value: 0 },
};

// Inject form override styles once at module load
(() => {
  if (typeof document === "undefined") return;
  if (document.getElementById("fco-styles")) return;
  const el = document.createElement("style");
  el.id = "fco-styles";
  el.textContent = `
    .fco .form-control,
    .fco .form-select {
      border: 1px solid rgba(60,60,67,0.18) !important;
      border-radius: 10px !important;
      padding: 11px 14px !important;
      font-size: 0.9375rem !important;
      font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", system-ui, sans-serif !important;
      color: #1C1C1E !important;
      background-color: #fff !important;
      background-image: none !important;
      box-shadow: none !important;
      height: auto !important;
      transition: border-color 0.15s ease, box-shadow 0.15s ease !important;
    }
    .fco .form-select {
      -webkit-appearance: none !important;
      appearance: none !important;
    }
    .fco .form-control:focus,
    .fco .form-select:focus {
      border-color: #007AFF !important;
      box-shadow: 0 0 0 3px rgba(0,122,255,0.12) !important;
      outline: none !important;
    }
    .fco .form-control.is-invalid,
    .fco .form-select.is-invalid,
    .fco form.was-validated .form-control:invalid,
    .fco form.was-validated .form-select:invalid {
      border-color: #FF3B30 !important;
      background-image: none !important;
      padding-right: 14px !important;
      box-shadow: none !important;
    }
    .fco .form-control.is-valid,
    .fco form.was-validated .form-control:valid {
      border-color: rgba(60,60,67,0.18) !important;
      background-image: none !important;
      box-shadow: none !important;
    }
    .fco .invalid-feedback {
      font-size: 0.73rem !important;
      color: #FF3B30 !important;
      font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", system-ui, sans-serif !important;
    }
    .fco .form-label {
      font-size: 0.8125rem !important;
      font-weight: 500 !important;
      color: #1C1C1E !important;
      margin-bottom: 6px !important;
      letter-spacing: -0.008em !important;
      font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", system-ui, sans-serif !important;
    }
    .fco .fco-icon-select {
      padding-left: 2.5rem !important;
      padding-right: 2.4rem !important;
    }
  `;
  document.head.appendChild(el);
})();

// ─── Shared atoms ─────────────────────────────────────────────────────────────

function SectionCard({ title, children, style = {} }) {
  return (
    <div
      style={{
        background: "#FFFFFF",
        borderRadius: 16,
        border: "1px solid rgba(60,60,67,0.07)",
        padding: "18px 20px",
        fontFamily: FONT,
        ...style,
      }}
    >
      {title && (
        <p
          style={{
            margin: "0 0 16px",
            fontSize: "0.9375rem",
            fontWeight: 600,
            color: "#1C1C1E",
            letterSpacing: "-0.015em",
          }}
        >
          {title}
        </p>
      )}
      {children}
    </div>
  );
}

function PhoneFeedback({ phone, verified }) {
  if (!phone) return null;
  return (
    <p
      style={{
        margin: "6px 0 0",
        fontSize: "0.73rem",
        fontWeight: 500,
        color: verified ? "#34C759" : "#FF3B30",
        display: "flex",
        alignItems: "center",
        gap: 4,
        fontFamily: FONT,
      }}
    >
      {verified ? (
        <><CheckCircle size={11} /> Phone number is valid</>
      ) : (
        "Phone number is invalid"
      )}
    </p>
  );
}

function FieldHint({ children }) {
  return (
    <p
      style={{
        margin: "5px 0 0",
        fontSize: "0.73rem",
        color: "rgba(60,60,67,0.45)",
        fontFamily: FONT,
      }}
    >
      {children}
    </p>
  );
}

function Skel({ width = "100%", height = 16, radius = 5 }) {
  return (
    <div
      className="animate-pulse"
      style={{
        width,
        height,
        borderRadius: radius,
        background: "rgba(60,60,67,0.08)",
        flexShrink: 0,
      }}
    />
  );
}

function ItemDivider() {
  return (
    <div
      style={{ height: 1, background: "rgba(60,60,67,0.07)", margin: "2px 0" }}
    />
  );
}

function CheckoutLineItem({ id, quantity, selectedOptions }) {
  const [product, setProduct] = useState(null);
  const [resolvedOptions, setResolvedOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchProduct(id)
      .then((data) => setProduct(data))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [id]);

  useEffect(() => {
    const valid = selectedOptions?.filter((s) => s.optionId && s.optionItemId);
    if (!valid?.length) return;
    Promise.all(valid.map((s) => fetchProductOptionItem(id, s.optionId, s.optionItemId)))
      .then((results) => setResolvedOptions(results.filter((r) => r?.value)))
      .catch(() => {});
  }, [id, selectedOptions]);

  const optionsAmount = resolvedOptions.reduce((sum, o) => sum + (o.amount || 0), 0);
  const cachedImageSrc = useCachedImageSrc(product?.imageSrc);

  if (isLoading || !product) {
    return (
      <div style={{ display: "flex", gap: 14, alignItems: "center", padding: "10px 0" }}>
        <Skel width={52} height={52} radius={10} />
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 7 }}>
          <Skel width="58%" height={14} radius={4} />
          <Skel width="36%" height={11} radius={4} />
        </div>
        <Skel width={58} height={15} radius={4} />
      </div>
    );
  }

  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "10px 0" }}>

      {/* Thumbnail */}
      <div style={{
        width: 52, height: 52, borderRadius: 10, flexShrink: 0,
        backgroundColor: "rgba(118,118,128,0.08)", overflow: "hidden",
      }}>
        <img
          src={cachedImageSrc}
          alt={product.name}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </div>

      {/* Info — takes all remaining width so pills never compete with price */}
      <div style={{ flex: 1, minWidth: 0 }}>

        {/* Top row: name + qty on left, line total on right */}
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 3 }}>
          <span style={{
            fontSize: "0.9375rem", fontWeight: 600, color: "#1C1C1E",
            letterSpacing: "-0.012em",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            flex: 1, minWidth: 0,
          }}>
            {product.name}
          </span>
          <span style={{
            fontSize: "0.75rem", fontWeight: 500,
            color: "rgba(60,60,67,0.4)", flexShrink: 0,
          }}>
            × {quantity}
          </span>
          <span style={{
            fontSize: "0.9375rem", fontWeight: 700,
            color: "#1C1C1E", letterSpacing: "-0.018em", flexShrink: 0,
          }}>
            {formatCurrency((product.price + optionsAmount) * quantity)}
          </span>
        </div>

        {/* Unit price micro-copy */}
        <p style={{
          margin: "0 0 7px",
          fontSize: "0.78125rem",
          color: "rgba(60,60,67,0.45)",
          letterSpacing: "-0.005em",
        }}>
          {formatCurrency(product.price + optionsAmount)} each
        </p>

        {/* Option pills — full width, wraps freely */}
        {resolvedOptions.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {resolvedOptions.map((opt, i) => (
              <span key={i} style={{
                display: "inline-flex", alignItems: "center", gap: 3,
                fontSize: "0.6875rem", fontWeight: 500,
                color: "#007AFF", backgroundColor: "rgba(0,122,255,0.08)",
                borderRadius: 999, padding: "3px 8px", whiteSpace: "nowrap",
                letterSpacing: "-0.005em",
              }}>
                {opt.value}
                {opt.amount > 0 && (
                  <span style={{ opacity: 0.6, fontWeight: 400 }}>
                    +{formatCurrency(opt.amount)}
                  </span>
                )}
              </span>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}

function FullSpinner({ message }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "60vh",
        gap: 14,
        fontFamily: FONT,
      }}
    >
      <Spinner
        animation="border"
        style={{ width: 26, height: 26, borderWidth: 2, color: "#007AFF" }}
      >
        <span className="visually-hidden">Loading…</span>
      </Spinner>
      {message && (
        <p
          style={{
            margin: 0,
            fontSize: "0.875rem",
            color: "rgba(60,60,67,0.5)",
          }}
        >
          {message}
        </p>
      )}
    </div>
  );
}

function PermissionScreen({ icon, iconBg, title, body, primaryLabel, onPrimary, onBack }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        padding: "40px 16px",
        fontFamily: FONT,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 440,
          background: "#FFFFFF",
          borderRadius: 20,
          border: "1px solid rgba(60,60,67,0.07)",
          padding: "40px 28px",
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            background: iconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 20px",
          }}
        >
          {icon}
        </div>
        <h2
          style={{
            margin: "0 0 10px",
            fontSize: "1.1875rem",
            fontWeight: 700,
            color: "#1C1C1E",
            letterSpacing: "-0.022em",
          }}
        >
          {title}
        </h2>
        <p
          style={{
            margin: "0 auto 28px",
            fontSize: "0.875rem",
            color: "rgba(60,60,67,0.55)",
            lineHeight: 1.5,
            maxWidth: 310,
          }}
        >
          {body}
        </p>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 10,
          }}
        >
          <button
            onClick={onPrimary}
            style={{
              width: "100%",
              maxWidth: 280,
              padding: "13px",
              borderRadius: 14,
              border: "none",
              background: "#007AFF",
              color: "#fff",
              fontSize: "0.9375rem",
              fontWeight: 600,
              letterSpacing: "-0.01em",
              cursor: "pointer",
              fontFamily: FONT,
            }}
          >
            {primaryLabel}
          </button>
          <button
            onClick={onBack}
            style={{
              background: "none",
              border: "none",
              color: "rgba(60,60,67,0.5)",
              fontSize: "0.875rem",
              fontWeight: 500,
              cursor: "pointer",
              padding: "8px",
              fontFamily: FONT,
            }}
          >
            Go Back
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Checkout ─────────────────────────────────────────────────────────────────

function Checkout() {
  const navigate = useNavigate();
  const location = useLocation();
  const formRef  = useRef(null);
  const { cartItems } = useShoppingCart();
  const { isOpen: isServiceOpen, opensAt: serviceOpensAt } = useServiceAvailability();

  const [user,               setUser]               = useState(null);
  const [authStateLoading,   setAuthStateLoading]   = useState(true);
  const [locationPermission, setLocationPermission] = useState({
    status: "prompt",
    isChecking: true,
    error: null,
  });
  const [formState, setFormState] = useState({
    firstName: "", lastName: "", phone: "",
    buildingType: "", unitFloor: "", buildingName: "", phoneTrans: "",
  });
  const [validationState, setValidationState] = useState({
    validated: false,
  });
  const [loadingState, setLoadingState] = useState({
    isSumLoading: true, isSubmitting: false,
  });
  const [locationState, setLocationState] = useState({
    userLocation: { lat: 0, lng: 0 },
    storeLocation: { lat: 0.03848896591259849, lng: 37.6529872354652 },
    rangeStatus: RANGE_STATUS.UNKNOWN,
  });
  const [routeState, setRouteState] = useState({
    routeLeg: null,
    routeDetails: EMPTY_ROUTE_DETAILS,
  });
  const [orderState, setOrderState] = useState({
    subSum: 0, deliveryAmount: 0, total: 0, taxAmount: 0, serviceFee: 0,
    originalDeliveryAmount: 0, isFreeDelivery: false, amountToFreeDelivery: 0,
    firstOrderDiscount: 0, walletCredit: 0,
  });
  const [isFirstOrder, setIsFirstOrder] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [isOpen, setIsOpen] = useState(true);

  const { restoredDraft, clearDraft } = useFormDraft("checkout", formState);

  useEffect(() => {
    if (restoredDraft) {
      setFormState((prev) => ({ ...prev, ...restoredDraft }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Callbacks ──────────────────────────────────────────────────────────────

  const getUserLocation = useCallback(
    () =>
      new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error("Geolocation is not supported by browser"));
          return;
        }
        navigator.geolocation.getCurrentPosition(
          ({ coords: { latitude, longitude } }) =>
            resolve({ lat: latitude, lng: longitude }),
          (error) => {
            console.error("Error getting user location:", error);
            let errorMessage = "Failed to get your location";
            switch (error.code) {
              case error.PERMISSION_DENIED:    errorMessage = "Location access was denied";            break;
              case error.POSITION_UNAVAILABLE: errorMessage = "Location information is unavailable";   break;
              case error.TIMEOUT:              errorMessage = "Location request timed out";             break;
            }
            reject(new Error(errorMessage));
          },
          { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
        );
      }),
    []
  );

  const checkLocationPermission = useCallback(async () => {
    try {
      setLocationPermission((prev) => ({ ...prev, isChecking: true, error: null }));
      if (!navigator.geolocation)
        throw new Error("Geolocation is not supported by your browser");
      const permissionStatus = await navigator.permissions.query({ name: "geolocation" });
      if (permissionStatus.state === "granted") {
        const location = await getUserLocation();
        setLocationState((prev) => ({ ...prev, userLocation: location }));
      }
      setLocationPermission((prev) => ({
        ...prev, status: permissionStatus.state, isChecking: false,
      }));
      permissionStatus.addEventListener("change", () => {
        setLocationPermission((prev) => ({ ...prev, status: permissionStatus.state }));
      });
    } catch (error) {
      console.error("Error checking location permission:", error);
      setLocationPermission((prev) => ({
        ...prev, isChecking: false, error: error.message,
      }));
    }
  }, [getUserLocation]);

  const requestLocationPermission = useCallback(async () => {
    try {
      setLocationPermission((prev) => ({ ...prev, isChecking: true, error: null }));
      const location = await getUserLocation();
      setLocationState((prev) => ({ ...prev, userLocation: location }));
      setLocationPermission((prev) => ({ ...prev, status: "granted", isChecking: false }));
    } catch (error) {
      console.error("Error requesting location permission:", error);
      setLocationPermission((prev) => ({
        ...prev, status: "denied", isChecking: false, error: error.message,
      }));
    }
  }, [getUserLocation]);

  const calculateDeliveryFee = useCallback(
    ({ distanceMeters, durationSeconds } = {}) => {
      const distanceKm = metersToKilometers(distanceMeters);
      const durationMinutes = secondsToMinutes(durationSeconds);

      if (!distanceMeters || !durationSeconds) {
        return DELIVERY_PRICING.BASE_FEE;
      }

      const baseFee = DELIVERY_PRICING.BASE_FEE;
      const distanceCharge = calculateDistanceCharge(distanceKm);
      const durationCharge = calculateDurationCharge(durationMinutes);

      const rawFee = baseFee + distanceCharge + durationCharge;
      const roundedFee = Math.round(rawFee);

      return clamp(
        roundedFee,
        DELIVERY_PRICING.MIN_DELIVERY_FEE,
        DELIVERY_PRICING.MAX_DELIVERY_FEE
      );
    },
    []
  );

  const handleInputChange = useCallback(
    (field) => (event) => {
      const value = event.target.value;
      setFormState((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleUserLocationChange = useCallback((location) => {
    setLocationState((prev) => ({ ...prev, userLocation: location }));
  }, []);

  const handleRouteChange = useCallback(({ leg, details }) => {
    setRouteState((prev) => ({ ...prev, routeLeg: leg, routeDetails: details }));
  }, []);

  const handleRouteLegChange = useCallback((leg) => {
    setRouteState((prev) => ({ ...prev, routeLeg: leg }));
  }, []);

  // ── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) setUser(user);
      else goToLogin(navigate, location);
      setAuthStateLoading(false);
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    if (user) checkLocationPermission();
  }, [user, checkLocationPermission]);

  const initializeData = useCallback(async () => {
    try {
      const idToken = user ? await user.getIdToken() : null;
      const [pin, sum, firstOrder, balance] = await Promise.all([
        fetchStoreLocation(cartItems[0]?.id),
        calculateSubtotal(cartItems),
        isFirstTimeCustomer(user?.uid, idToken),
        idToken ? fetchWalletBalance(idToken) : Promise.resolve(0),
      ]);
      const pinLat = pin?.lat ?? pin?.latitude;
      const pinLng = pin?.lng ?? pin?.longitude;
      const hasValidPin =
        typeof pinLat === "number" && typeof pinLng === "number" &&
        pinLat !== 0 && pinLng !== 0;

      if (hasValidPin) {
        setLocationState((prev) => ({ ...prev, storeLocation: { lat: pinLat, lng: pinLng } }));
      } else {
        console.error("Ignoring invalid store location from fetchStoreLocation:", pin);
      }
      setIsFirstOrder(firstOrder);
      setWalletBalance(balance);
      const { discount: firstOrderDiscount } = applyFirstOrderDiscount(sum, firstOrder);
      setOrderState((prev) => priceOrder({ ...prev, subSum: sum, firstOrderDiscount }, balance));
      setLoadingState((prev) => ({ ...prev, isSumLoading: false }));
    } catch (error) {
      console.error("Error initializing data:", error);
      toast.error("Failed to initialize checkout data");
    }
  }, [cartItems, user]);

  useEffect(() => {
    if (user) initializeData();
  }, [user, initializeData]);

  const updateDeliveryFee = useCallback(async () => {
    const distanceMeters = routeState.routeDetails.distance.value;
    const durationSeconds = routeState.routeDetails.duration.value;

    const hasDistanceData =
      Boolean(routeState.routeLeg) &&
      typeof distanceMeters === "number" &&
      typeof durationSeconds === "number";
    const isWithinDistanceRange =
      hasDistanceData &&
      distanceMeters <= DELIVERY_PRICING.MAX_DELIVERY_DISTANCE_KM * METERS_PER_KILOMETER;

    setLocationState((prev) => ({
      ...prev,
      rangeStatus: !hasDistanceData
        ? RANGE_STATUS.UNKNOWN
        : isWithinDistanceRange
        ? RANGE_STATUS.WITHIN_RANGE
        : RANGE_STATUS.OUT_OF_RANGE,
    }));

    setLoadingState((prev) => ({ ...prev, isSumLoading: true }));
    try {
      const sum = await calculateSubtotal(cartItems);
      const rawDeliveryFee = calculateDeliveryFee({ distanceMeters, durationSeconds });
      const thresholdDelivery = applyFreeDeliveryDiscount(sum, rawDeliveryFee);
      const firstOrderDelivery = applyFirstOrderFreeDelivery(rawDeliveryFee, isFirstOrder);
      const isFreeDelivery = thresholdDelivery.isFreeDelivery || firstOrderDelivery.isFreeDelivery;
      const deliveryFee = isFreeDelivery ? 0 : rawDeliveryFee;
      const originalDeliveryFee = thresholdDelivery.originalDeliveryFee;
      const amountToFreeDelivery = isFreeDelivery ? 0 : thresholdDelivery.amountToFreeDelivery;
      const { discount: firstOrderDiscount } = applyFirstOrderDiscount(sum, isFirstOrder);

      setOrderState((prev) => priceOrder({
        ...prev,
        subSum: sum,
        deliveryAmount: deliveryFee,
        originalDeliveryAmount: originalDeliveryFee,
        isFreeDelivery,
        amountToFreeDelivery,
        firstOrderDiscount,
      }, walletBalance));
    } catch (error) {
      console.error("Error calculating delivery fee:", error);
    } finally {
      setLoadingState((prev) => ({ ...prev, isSumLoading: false }));
    }
  }, [routeState.routeDetails, routeState.routeLeg, cartItems, calculateDeliveryFee, isFirstOrder, walletBalance]);

  useEffect(() => {
    updateDeliveryFee();
  }, [updateDeliveryFee]);

  const refreshCheckoutData = useCallback(async () => {
    await Promise.all([initializeData(), updateDeliveryFee()]);
  }, [initializeData, updateDeliveryFee]);

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async (event) => {
    event.preventDefault();
    setValidationState((prev) => ({ ...prev, validated: true }));
    setLoadingState((prev) => ({ ...prev, isSubmitting: true }));
    try {
      if (!user) throw new Error("User not authenticated");
      const formErrors = Object.keys(FORM_FIELDS)
        .map((field) => getFieldError(field, formState[field]))
        .filter(Boolean);
      if (formErrors.length > 0) throw new Error(formErrors.join("\n"));

      const token = await user.getIdToken();
      clearDraft();
      navigate("/checkout/progress", {
        state: {
          order: {
            ...formState,
            location: locationState.userLocation,
            cart: cartItems,
            cost: {
              subTotal:     orderState.subSum,
              serviceFee:   orderState.serviceFee,
              deliveryFee:  orderState.deliveryAmount,
              tax:          orderState.taxAmount,
              discount:     orderState.firstOrderDiscount,
              walletCredit: orderState.walletCredit,
              total:        orderState.total,
            },
            uid:   user.uid,
            route: routeState.routeDetails,
          },
          email: user.email,
          token,
        },
      });
    } catch (error) {
      console.error("Order submission error:", error);
      toast.error(error.message || "Failed to place order");
    } finally {
      setLoadingState((prev) => ({ ...prev, isSubmitting: false }));
    }
  };

  const openOrderSum  = () => setIsOpen(true);
  const closeOrderSum = () => setIsOpen(false);

  // ── Guards ─────────────────────────────────────────────────────────────────

  if (authStateLoading) return <FullSpinner />;
  if (!user) return null;

  if (!isServiceOpen) {
    return (
      <PermissionScreen
        icon={<Clock size={30} color="#FF9500" />}
        iconBg="rgba(255,149,0,0.1)"
        title="We're Currently Closed"
        body={
          serviceOpensAt
            ? `Teco isn't taking orders right now. We reopen at ${serviceOpensAt}.`
            : "Teco isn't taking orders right now. Please check back soon."
        }
        primaryLabel="Browse Stores"
        onPrimary={() => navigate("/")}
        onBack={() => navigate(-1)}
      />
    );
  }

  if (locationPermission.isChecking)
    return <FullSpinner message="Checking location access…" />;

  if (locationPermission.status === "denied" || locationPermission.error) {
    return (
      <PermissionScreen
        icon={<ExclamationTriangle size={30} color="#FF9500" />}
        iconBg="rgba(255,149,0,0.1)"
        title="Location Access Required"
        body={
          locationPermission.error ||
          "We need access to your location to provide delivery services. Please enable location access in your browser settings."
        }
        primaryLabel="Try Again"
        onPrimary={requestLocationPermission}
        onBack={() => navigate(-1)}
      />
    );
  }

  if (locationPermission.status === "prompt") {
    return (
      <PermissionScreen
        icon={<GeoAlt size={30} color="#007AFF" />}
        iconBg="rgba(0,122,255,0.1)"
        title="Enable Location Access"
        body="We need access to your location to provide delivery services. Please allow location access when prompted."
        primaryLabel="Enable Location"
        onPrimary={requestLocationPermission}
        onBack={() => navigate(-1)}
      />
    );
  }

  // ── Derived ────────────────────────────────────────────────────────────────

  const BuildingIcon =
    { house: House, apartment: Building, office: Buildings }[formState.buildingType] ?? Building;

  const firstNameError = getInlineFieldError("firstName", formState.firstName);
  const lastNameError  = getInlineFieldError("lastName", formState.lastName);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <PullToRefreshWrapper onRefresh={refreshCheckoutData}>
    <Seo {...generateNoIndexSEO("Checkout")} />
    <div className="fco" style={{ fontFamily: FONT, padding: "12px 0 48px" }}>
      <div className="container" style={{ maxWidth: 1040 }}>
        <div className="row g-3 g-md-4">

          {/* ── Form column ── */}
          <div className="col-12 col-md-7">
            <Form
              ref={formRef}
              noValidate
              validated={validationState.validated}
              onSubmit={handleSubmit}
              style={{ display: "flex", flexDirection: "column", gap: 12 }}
            >
              {/* Contact */}
              <SectionCard title="Contact">
                <Row className="g-3">
                  <Form.Group as={Col} xs={12} lg={6} controlId="formCheckoutFName">
                    <Form.Label>First Name</Form.Label>
                    <Form.Control
                      type="text"
                      required
                      placeholder="First name"
                      value={formState.firstName}
                      onChange={handleInputChange("firstName")}
                      isInvalid={Boolean(firstNameError)}
                    />
                    {firstNameError && (
                      <Form.Control.Feedback type="invalid">
                        {firstNameError}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>

                  <Form.Group as={Col} xs={12} lg={6} controlId="formCheckoutLName">
                    <Form.Label>Last Name</Form.Label>
                    <Form.Control
                      type="text"
                      required
                      placeholder="Last name"
                      value={formState.lastName}
                      onChange={handleInputChange("lastName")}
                      isInvalid={Boolean(lastNameError)}
                    />
                    {lastNameError && (
                      <Form.Control.Feedback type="invalid">
                        {lastNameError}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>

                  <Form.Group as={Col} xs={12} controlId="formCheckoutPhone">
                    <Form.Label>Phone Number</Form.Label>
                    <Form.Control
                      type="tel"
                      required
                      placeholder="07** *** ***"
                      value={formState.phone}
                      onChange={handleInputChange("phone")}
                    />
                    <PhoneFeedback
                      phone={formState.phone}
                      verified={isPhone(formState.phone)}
                    />
                  </Form.Group>
                </Row>
              </SectionCard>

              {/* Delivery location */}
              <SectionCard title="Delivery Location">
                <MapInterface
                  userLocation={locationState.userLocation}
                  setUserLocation={handleUserLocationChange}
                  storeLocation={locationState.storeLocation}
                  onRouteChange={handleRouteChange}
                  setRouteLeg={handleRouteLegChange}
                />

                <Row className="g-3 mt-1">
                  <Form.Group as={Col} xs={12} controlId="buildingType">
                    <Form.Label>Building Type</Form.Label>
                    <div style={{ position: "relative" }}>
                      <Form.Select
                        className="fco-icon-select"
                        value={formState.buildingType}
                        onChange={(e) =>
                          setFormState((prev) => ({ ...prev, buildingType: e.target.value }))
                        }
                        required
                      >
                        <option value="">Select building type</option>
                        <option value="house">House</option>
                        <option value="apartment">Apartment</option>
                        <option value="office">Office</option>
                        <option value="other">Other</option>
                      </Form.Select>
                      <div
                        style={{
                          position: "absolute", left: 13, top: "50%",
                          transform: "translateY(-50%)",
                          pointerEvents: "none",
                          color: "rgba(60,60,67,0.4)",
                          display: "flex", alignItems: "center",
                        }}
                      >
                        <BuildingIcon size={15} />
                      </div>
                      <div
                        style={{
                          position: "absolute", right: 12, top: "50%",
                          transform: "translateY(-50%)",
                          pointerEvents: "none",
                          color: "rgba(60,60,67,0.38)",
                          display: "flex", alignItems: "center",
                        }}
                      >
                        <ChevronDown size={13} />
                      </div>
                    </div>
                  </Form.Group>

                  <Form.Group as={Col} xs={12} md={6} controlId="unitFloor">
                    <Form.Label>Unit / Floor</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g., Unit 5, Floor 3"
                      value={formState.unitFloor}
                      onChange={(e) =>
                        setFormState((prev) => ({ ...prev, unitFloor: e.target.value }))
                      }
                    />
                    <FieldHint>Enter your unit number or floor</FieldHint>
                  </Form.Group>

                  <Form.Group as={Col} xs={12} md={6} controlId="buildingName">
                    <Form.Label>Building / Estate Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g., Green Valley Apartments"
                      value={formState.buildingName}
                      onChange={(e) =>
                        setFormState((prev) => ({ ...prev, buildingName: e.target.value }))
                      }
                    />
                    <FieldHint>Name of your building or estate</FieldHint>
                  </Form.Group>
                </Row>
              </SectionCard>

              {/* Payment */}
              <SectionCard title="Payment">
                <p
                  style={{
                    margin: "0 0 14px",
                    fontSize: "0.8125rem",
                    color: "rgba(60,60,67,0.5)",
                  }}
                >
                  This number will be used to complete payment
                </p>
                <div
                  style={{
                    border: "1px solid rgba(60,60,67,0.1)",
                    borderRadius: 12,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      padding: "12px 14px",
                      background: "rgba(242,242,247,0.6)",
                      borderBottom: "1px solid rgba(60,60,67,0.07)",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                    }}
                  >
                    <Image src={mpesaLogo} width={40} height={24} rounded />
                    <span
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: 600,
                        color: "#1C1C1E",
                      }}
                    >
                      M-Pesa
                    </span>
                  </div>
                  <div style={{ padding: 14 }}>
                    <Form.Group controlId="payPhoneNo">
                      <Form.Control
                        type="text"
                        placeholder="07** *** ***"
                        required
                        value={formState.phoneTrans}
                        onChange={handleInputChange("phoneTrans")}
                      />
                    </Form.Group>
                    <PhoneFeedback
                      phone={formState.phoneTrans}
                      verified={isPhone(formState.phoneTrans)}
                    />
                  </div>
                </div>
              </SectionCard>

              {/* Submit */}
              <CheckoutSubmitButton
                isSubmitting={loadingState.isSubmitting}
                cartItemCount={cartItems.length}
                rangeStatus={locationState.rangeStatus}
                total={orderState.total}
                fontFamily={FONT}
              />
            </Form>
          </div>

          {/* ── Order summary column ── */}
          {cartItems.length > 0 && (
            <div className="col-12 col-md-5">
              <div style={{ position: "sticky", top: 24 }}>
                <div
                  style={{
                    background: "#FFFFFF",
                    borderRadius: 16,
                    border: "1px solid rgba(60,60,67,0.07)",
                    fontFamily: FONT,
                    overflow: "hidden",
                  }}
                >
                  {/* Mobile toggle header */}
                  <button
                    className="d-flex d-md-none"
                    onClick={isOpen ? closeOrderSum : openOrderSum}
                    style={{
                      width: "100%",
                      padding: "16px 20px",
                      background: "none",
                      border: "none",
                      alignItems: "center",
                      justifyContent: "space-between",
                      cursor: "pointer",
                      fontFamily: FONT,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Cart size={15} color="#007AFF" />
                      <span
                        style={{
                          fontSize: "0.9375rem",
                          fontWeight: 600,
                          color: "#1C1C1E",
                          letterSpacing: "-0.015em",
                        }}
                      >
                        {isOpen ? "Hide" : "Show"} order summary
                      </span>
                      {isOpen
                        ? <ArrowUpShort size={19} color="#8E8E93" />
                        : <ArrowDownShort size={19} color="#8E8E93" />}
                    </div>
                    {loadingState.isSumLoading ? (
                      <Skel width={60} height={18} />
                    ) : (
                      <span
                        style={{
                          fontSize: "1rem",
                          fontWeight: 700,
                          color: "#1C1C1E",
                          letterSpacing: "-0.018em",
                        }}
                      >
                        {formatCurrency(orderState.total)}
                      </span>
                    )}
                  </button>

                  {/* Desktop title — always visible */}
                  <div
                    className="d-none d-md-flex"
                    style={{ padding: "18px 20px 0", alignItems: "center" }}
                  >
                    <span
                      style={{
                        fontSize: "0.9375rem",
                        fontWeight: 600,
                        color: "#1C1C1E",
                        letterSpacing: "-0.015em",
                      }}
                    >
                      Order Summary
                    </span>
                  </div>

                  {/* Content — collapses on mobile, always shown on desktop */}
                  <div
                    className="d-md-block"
                    style={!isOpen ? { display: "none" } : {}}
                  >
                    <div style={{ padding: "16px 20px 20px" }}>
                      {/* Mobile separator */}
                      <div
                        className="d-md-none"
                        style={{
                          height: 1,
                          background: "rgba(60,60,67,0.07)",
                          marginBottom: 16,
                        }}
                      />

                      {/* Cart items */}
                      <div style={{ marginBottom: 16 }}>
                        {cartItems.map((item, index) => (
                          <div key={item.id}>
                            <CheckoutLineItem {...item} />
                            {index < cartItems.length - 1 && <ItemDivider />}
                          </div>
                        ))}
                      </div>

                      {/* Cost breakdown */}
                      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                        {[
                          { label: "Subtotal", value: orderState.subSum      },
                          { label: "Service Fee", value: orderState.serviceFee },
                        ].map(({ label, value }) => (
                          <div
                            key={label}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "0.875rem",
                                color: "rgba(60,60,67,0.52)",
                              }}
                            >
                              {label}
                            </span>
                            {loadingState.isSumLoading ? (
                              <Skel width={56} height={14} radius={4} />
                            ) : (
                              <span
                                style={{
                                  fontSize: "0.875rem",
                                  fontWeight: 500,
                                  color: "#1C1C1E",
                                }}
                              >
                                {formatCurrency(value)}
                              </span>
                            )}
                          </div>
                        ))}

                        {!loadingState.isSumLoading && orderState.firstOrderDiscount > 0 && (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "0.875rem",
                                fontWeight: 500,
                                color: "#34C759",
                              }}
                            >
                              First order discount
                            </span>
                            <span
                              style={{
                                fontSize: "0.875rem",
                                fontWeight: 600,
                                color: "#34C759",
                              }}
                            >
                              −{formatCurrency(orderState.firstOrderDiscount)}
                            </span>
                          </div>
                        )}

                        {!loadingState.isSumLoading && orderState.walletCredit > 0 && (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "0.875rem",
                                fontWeight: 500,
                                color: "#34C759",
                              }}
                            >
                              Wallet credit
                            </span>
                            <span
                              style={{
                                fontSize: "0.875rem",
                                fontWeight: 600,
                                color: "#34C759",
                              }}
                            >
                              −{formatCurrency(orderState.walletCredit)}
                            </span>
                          </div>
                        )}

                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "0.875rem",
                              color: "rgba(60,60,67,0.52)",
                            }}
                          >
                            Delivery
                          </span>
                          {loadingState.isSumLoading ? (
                            <Skel width={56} height={14} radius={4} />
                          ) : orderState.isFreeDelivery ? (
                            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <span
                                style={{
                                  fontSize: "0.8125rem",
                                  color: "rgba(60,60,67,0.35)",
                                  textDecoration: "line-through",
                                }}
                              >
                                {formatCurrency(orderState.originalDeliveryAmount)}
                              </span>
                              <span
                                style={{
                                  fontSize: "0.6875rem",
                                  fontWeight: 700,
                                  color: "#34C759",
                                  background: "rgba(52,199,89,0.1)",
                                  borderRadius: 999,
                                  padding: "2px 8px",
                                  letterSpacing: "0.01em",
                                }}
                              >
                                FREE
                              </span>
                            </span>
                          ) : (
                            <span
                              style={{
                                fontSize: "0.875rem",
                                fontWeight: 500,
                                color: "#1C1C1E",
                              }}
                            >
                              {formatCurrency(orderState.deliveryAmount)}
                            </span>
                          )}
                        </div>

                        {!loadingState.isSumLoading &&
                          !orderState.isFreeDelivery &&
                          orderState.amountToFreeDelivery > 0 && (
                            <p
                              style={{
                                margin: "-2px 0 0",
                                fontSize: "0.75rem",
                                fontWeight: 500,
                                color: "#007AFF",
                              }}
                            >
                              Add {formatCurrency(orderState.amountToFreeDelivery)} more for free delivery
                            </p>
                          )}

                        <div
                          style={{
                            height: 1,
                            background: "rgba(60,60,67,0.07)",
                            margin: "3px 0",
                          }}
                        />

                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "0.9375rem",
                              fontWeight: 700,
                              color: "#1C1C1E",
                              letterSpacing: "-0.015em",
                            }}
                          >
                            Total
                          </span>
                          {loadingState.isSumLoading ? (
                            <Skel width={72} height={20} radius={4} />
                          ) : (
                            <span
                              style={{
                                fontSize: "1.0625rem",
                                fontWeight: 700,
                                color: "#007AFF",
                                letterSpacing: "-0.02em",
                              }}
                            >
                              {formatCurrency(orderState.total)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
    </PullToRefreshWrapper>
  );
}

export default Checkout;
